import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../../i18n/useLanguage';
import { UserSearch } from './UserSearch';
import type { UserSearchResult } from '../../types/user';
import './NewConversationModal.css';

interface NewConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectUser: (user: UserSearchResult) => void;
}

export const NewConversationModal: React.FC<NewConversationModalProps> = ({
  isOpen,
  onClose,
  onSelectUser,
}) => {
  const { t } = useLanguage();
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    document.addEventListener('keydown', handleEscape);

    // Focus the modal for accessibility
    modalRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSelectUser = (user: UserSearchResult) => {
    onSelectUser(user);
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="new-conversation-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-conversation-title"
    >
      <div 
        ref={modalRef}
        className="new-conversation-modal" 
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        <div className="new-conversation-modal-header">
          <h2 id="new-conversation-title">{t('messages.modal.title')}</h2>
          <button 
            className="new-conversation-modal-close" 
            onClick={onClose}
            aria-label={t('messages.modal.closeAria')}
            type="button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="new-conversation-modal-content">
          <p className="new-conversation-modal-description">
            {t('messages.modal.description')}
          </p>
          <UserSearch
            onSelectUser={handleSelectUser}
            placeholder={t('messages.search.placeholder')}
            minQueryLength={2}
          />
        </div>
      </div>
    </div>
  );
};
